import {
  UIEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type ScrollState =
  | {
      state: 'initial';
      previous: undefined;
      isNearBottom: false;
      current: 0;
    }
  | {
      state: 'scrolled';
      previous: number;
      isNearBottom: boolean;
      current: number;
    };

export const initialScrollState: ScrollState = {
  state: 'initial',
  previous: undefined,
  isNearBottom: false,
  current: 0,
};

export type UseScrollOptions = {
  threshold?: number;
};

export const useScroll = (options?: UseScrollOptions) => {
  const [scrollPosition, setScroll] = useState<ScrollState>(initialScrollState);
  const threshold = options?.threshold ?? 0.9;

  useEffect(() => {
    const scrollHandler = () => {
      const scrollingElement = document.scrollingElement;
      const current = window.scrollY;
      setScroll(state => ({
        state: 'scrolled',
        previous: state.current,
        isNearBottom: !scrollingElement
          ? true
          : window.innerHeight + current >=
            scrollingElement.scrollHeight * threshold,
        current,
      }));
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
    return () => window.removeEventListener('scroll', scrollHandler);
  }, [threshold]);

  return scrollPosition;
};

export type UseComponentScrollOptions = {
  reversed?: boolean;
  threshold?: number;
};

export type UseComponentScrollReturn = {
  scrollPosition: ScrollState;
  onScroll: UIEventHandler<HTMLElement>;
};

export const useComponentScroll = (options?: UseComponentScrollOptions) => {
  const [scrollPosition, setScroll] = useState<ScrollState>(initialScrollState);
  const reversed = options?.reversed ?? false;
  const threshold = options?.threshold ?? 0.9;

  const onScroll: UIEventHandler<HTMLElement> = useCallback(
    event => {
      let scrollY = event.currentTarget.scrollTop;
      if (reversed) {
        scrollY = -scrollY;
      }
      const componentHeight =
        event.currentTarget.scrollHeight - event.currentTarget.clientHeight;

      setScroll(state => ({
        state: 'scrolled',
        previous: state.current,
        isNearBottom: scrollY >= componentHeight * threshold,
        current: scrollY,
      }));
    },
    [reversed, threshold],
  );

  return useMemo(
    () => ({
      scrollPosition,
      onScroll,
    }),
    [scrollPosition, onScroll],
  );
};
